<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="800"
    content-class="po-confirm-dialog"
    @click:outside="close"
  >
    <v-card class="po-confirm-card">
      <v-toolbar color="primary" class="white--text" flat height="48">
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          {{ purchaseOrderLabel }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="po-confirm-dialog__body pa-0">
        <section class="po-confirm-section px-6 pt-5 pb-4">
          <div class="po-confirm-content">
            <div class="po-confirm-section__header d-flex justify-space-between align-center">
              <h3 class="po-confirm-section__title mb-0">
                {{ $t("memberDataSection.purchaseOrderDetail.detailConfirmation") }}
              </h3>
              <v-btn
                fab
                x-small
                depressed
                color="primary"
                class="po-confirm-section__edit-btn white--text"
                @click="onEdit"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>

            <div
              v-for="(row, rowIndex) in fieldRows"
              :key="`po-confirm-row-${rowIndex}`"
              class="po-confirm-grid-row"
            >
              <div
                v-for="field in row"
                :key="field.key"
                class="po-confirm-grid-cell"
                :class="{ 'po-confirm-grid-cell--full': field.fullWidth }"
              >
                <div class="po-confirm-field-label">{{ field.label }}</div>
                <div class="po-confirm-field-value">{{ field.value }}</div>
              </div>
            </div>

            <div class="po-confirm-photos-block">
              <h3 class="po-confirm-plain-title mb-0">
                {{ $t("memberDataSection.purchaseOrderDetail.photos") }}
              </h3>
              <div class="po-confirm-media-row">
                <div class="po-confirm-photo-wrap rounded">
                  <img
                    v-if="photoUrl"
                    :src="photoUrl"
                    alt=""
                    class="po-confirm-photo"
                  />
                  <div v-else class="po-confirm-photo-placeholder" aria-hidden="true">
                    <v-icon size="40" color="#bdbdbd">mdi-image-outline</v-icon>
                  </div>
                </div>
              </div>
              <v-btn
                outlined
                color="primary"
                height="36"
                class="po-confirm-quality-btn text-uppercase px-5"
                @click="$emit('quality-grade')"
              >
                <v-icon left small>mdi-seal</v-icon>
                {{ $t("memberDataSection.greenCoffee.createQualityGrade") }}
              </v-btn>
            </div>
          </div>
        </section>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="px-6 py-4 po-confirm-footer">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          height="36"
          class="text-uppercase px-5 mr-3"
          :disabled="confirming"
          @click="close"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          height="36"
          depressed
          class="text-uppercase white--text px-5"
          :loading="confirming"
          :disabled="confirming"
          @click="$emit('confirm')"
        >
          {{ $t("memberDataSection.purchaseOrderDetail.confirmBtn") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { isKenyaClient } from "@/utils";
export default {
  name: "PurchaseOrderConfirmDialog",
  props: {
    value: { type: Boolean, default: false },
    fieldRows: { type: Array, default: () => [] },
    photoUrl: { type: String, default: null },
    confirming: { type: Boolean, default: false },
  },
  data() {
    return {
      visible: this.value,
    };
  },
  computed: {
    purchaseOrderLabel() {
      return isKenyaClient() ? this.$t("memberDataSection.cherryCollection") : this.$t("memberDataSection.purchaseOrderDetail.confirmDialogTitle");
    },
  },
  watch: {
    value(v) {
      this.visible = v;
    },
    visible(v) {
      this.$emit("input", v);
    },
  },
  methods: {
    close() {
      this.visible = false;
      this.$emit("close");
    },
    onEdit() {
      this.visible = false;
      this.$emit("edit");
    },
  },
};
</script>

<style lang="scss" scoped>
.po-confirm-card {
  border-radius: 4px;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
  background: #ffffff;
}

.po-confirm-dialog__body {
  max-height: min(500px, calc(90vh - 120px));
  overflow-y: scroll;
  background: #ffffff;
}

.po-confirm-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  padding: 0;
  width: 100%;
}

.po-confirm-section__header {
  width: 100%;
}

.po-confirm-section__title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
  font-size: 16px;
  line-height: 1.35;
}

.po-confirm-section__edit-btn {
  box-shadow: none;
}

.po-confirm-plain-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
}

.po-confirm-grid-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;
  width: 100%;
}

.po-confirm-grid-cell {
  min-width: 0;
}

.po-confirm-grid-cell--full {
  grid-column: 1 / -1;
  max-width: calc(33.333% - 14px);
}

.po-confirm-field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.35;
  margin-bottom: 2px;
}

.po-confirm-field-value {
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  word-break: break-word;
}

.po-confirm-photos-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
}

.po-confirm-media-row {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;
}

.po-confirm-photo-wrap {
  width: 120px;
  height: 120px;
  flex: 0 0 auto;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.po-confirm-photo {
  display: block;
  width: 120px;
  height: 120px;
  object-fit: cover;
}

.po-confirm-photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.po-confirm-quality-btn {
  align-self: flex-start;
}

.po-confirm-footer {
  background: #ffffff;
}
</style>

<style>
.po-confirm-dialog {
  overflow: visible;
}

.po-confirm-dialog > .v-card {
  background: #ffffff !important;
}
</style>
